import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(() => {
        const savedUser = localStorage.getItem('user');
        return savedUser ? JSON.parse(savedUser) : null;
    });
    const [showLoginModal, setShowLoginModal] = useState(false);

    // Check if token is expired
    const checkTokenExpiration = useCallback(() => {
        const token = localStorage.getItem('token');
        if (!token) return false;

        try {
            // Validate token format
            const parts = token.split('.');
            if (parts.length !== 3) {
                console.error('Invalid token format');
                return false;
            }

            const payload = JSON.parse(atob(parts[1]));

            // Check if token has expiration
            if (!payload.exp) {
                console.error('Token missing expiration');
                return false;
            }

            return Date.now() < payload.exp * 1000;
        } catch (error) {
            console.error('Error checking token expiration:', error);
            return false;
        }
    }, []);

    const login = useCallback(async (userData) => {
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('token', userData.token);
        setShowLoginModal(false);
    }, []);

    const logout = useCallback(() => {
        setUser(null);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        setShowLoginModal(false);
    }, []);

    // Fetch user subscriptions
    const fetchUserSubscriptions = useCallback(async () => {
        // Don't fetch if user is not logged in
        if (!user || !checkTokenExpiration()) return;

        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/subscriptions/getMySubscriptions`);
            if (response.data.data.subscriptions) {
                // Only update if subscriptions have changed
                if (JSON.stringify(user.subscriptions) !== JSON.stringify(response.data.data.subscriptions)) {
                    const updatedUser = {
                        ...user,
                        subscriptions: response.data.data.subscriptions,
                    };
                    setUser(updatedUser);
                    localStorage.setItem('user', JSON.stringify(updatedUser));
                }
            }
        } catch (error) {
            console.error('Error fetching subscriptions:', error);
            // Don't logout on subscription fetch errors - this was too aggressive
            // Only logout if we get a 401 AND the token is actually expired
            if (error.response?.status === 401 && !checkTokenExpiration()) {
                console.warn('Subscription fetch failed due to expired token');
                logout();
            }
            // For other errors (like "No subscriptions found"), just log them
        }
    }, [user, checkTokenExpiration, logout]);

    // Handle initial subscription fetch and periodic refresh
    useEffect(() => {
        let subscriptionInterval;

        const initializeSubscriptions = async () => {
            if (user && checkTokenExpiration()) {
                await fetchUserSubscriptions();
                // Set up periodic refresh
                subscriptionInterval = setInterval(() => {
                    fetchUserSubscriptions();
                }, 5 * 60 * 1000);
            }
        };

        initializeSubscriptions();

        // Cleanup
        return () => {
            if (subscriptionInterval) {
                clearInterval(subscriptionInterval);
            }
        };
    }, [user?.id]); // Only re-run if user ID changes, not on every subscription update

    // Check token expiration periodically with grace period
    useEffect(() => {
        const checkAuth = () => {
            // Only check if user is logged in
            if (!user) return;

            const token = localStorage.getItem('token');
            if (!token) {
                logout();
                return;
            }

            // Validate token and check expiration
            const isValid = checkTokenExpiration();

            if (!isValid) {
                console.warn('Token expired or invalid - logging out user');
                logout();
            }
        };

        // Check less frequently (every 5 minutes instead of every minute)
        // This reduces the aggressiveness of auto-logout
        const authInterval = setInterval(checkAuth, 5 * 60 * 1000);

        // Don't perform initial check immediately on mount
        // This prevents logout on page refresh during valid sessions

        return () => clearInterval(authInterval);
    }, [user, checkTokenExpiration, logout]);

    const value = {
        user,
        login,
        logout,
        showLoginModal,
        setShowLoginModal,
        isAuthenticated: !!user && checkTokenExpiration(),
        checkTokenExpiration,
        refreshSubscriptions: fetchUserSubscriptions,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
